import { useState, useMemo, useCallback } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { transactions as initialTransactions, type Transaction } from '@/mocks/dashboard';

const emptyForm = { userName: '', propertyTitle: '', amount: '', type: 'Listing Fee', status: 'Paid', paymentMethod: 'UPI', notes: '' };

export default function TransactionsPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Transaction | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Transaction | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [txData, setTxData] = useState<Transaction[]>(initialTransactions);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const clearMsg = () => setTimeout(() => setActionMsg(null), 3000);

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const filteredIds = filtered.map(t => t.id);
    const allSelected = filteredIds.length > 0 && filteredIds.every(id => selectedIds.includes(id));
    setSelectedIds(prev => allSelected ? prev.filter(id => !filteredIds.includes(id)) : Array.from(new Set([...prev, ...filteredIds])));
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setTxData(prev => prev.filter(t => !selectedIds.includes(t.id)));
    setActionMsg({ type: 'success', text: `${selectedIds.length} transaction(s) deleted.` });
    setSelectedIds([]);
    clearMsg();
  };

  const handleBulkStatus = (status: string) => {
    if (selectedIds.length === 0) return;
    setTxData(prev => prev.map(t => selectedIds.includes(t.id) ? { ...t, status } : t));
    setActionMsg({ type: 'success', text: `${selectedIds.length} transaction(s) marked as ${status}.` });
    setSelectedIds([]);
    clearMsg();
  };

  const filtered = useMemo(() => {
    let result = [...txData];
    if (statusFilter) result = result.filter(t => t.status === statusFilter);
    if (typeFilter) result = result.filter(t => t.type === typeFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(t => t.userName.toLowerCase().includes(q) || t.propertyTitle.toLowerCase().includes(q) || String(t.id).includes(q));
    }
    return result;
  }, [txData, statusFilter, typeFilter, search]);

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      'Paid': 'bg-teal-500/15 text-teal-400 border-teal-500/25',
      'Pending': 'bg-accent-100 text-accent-700 border-accent-500/25',
      'Failed': 'bg-rose-500/15 text-rose-400 border-rose-500/25',
      'Refunded': 'bg-blue-500/15 text-blue-400 border-blue-500/25',
    };
    return map[status] || 'bg-background-200/50 text-foreground-500';
  };

  const typeBadge = (type: string) => {
    const map: Record<string, string> = {
      'Listing Fee': 'bg-primary-500/15 text-primary-700',
      'Featured Upgrade': 'bg-accent-100 text-accent-700',
      'Subscription': 'bg-secondary-500/15 text-secondary-700',
    };
    return map[type] || 'bg-background-200/50 text-foreground-500';
  };

  const totalRevenue = filtered.filter(t => t.status === 'Paid').reduce((sum, t) => sum + t.amount, 0);

  const openCreate = () => {
    setEditTarget(null);
    setFormData(emptyForm);
    setShowAddModal(true);
  };

  const openEdit = (tx: Transaction) => {
    setFormData({
      userName: tx.userName,
      propertyTitle: tx.propertyTitle,
      amount: String(tx.amount),
      type: tx.type,
      status: tx.status,
      paymentMethod: tx.paymentMethod,
      notes: '',
    });
    setEditTarget(tx);
    setShowAddModal(true);
  };

  const handleSubmit = useCallback(() => {
    if (!formData.userName || !formData.propertyTitle || !formData.amount) {
      setActionMsg({ type: 'error', text: 'User, property, and amount are required.' });
      clearMsg();
      return;
    }

    const amount = Number(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setActionMsg({ type: 'error', text: 'Amount must be a positive number.' });
      clearMsg();
      return;
    }

    if (editTarget) {
      setTxData(prev => prev.map(t => t.id === editTarget.id ? {
        ...t,
        userName: formData.userName,
        propertyTitle: formData.propertyTitle,
        amount,
        type: formData.type,
        status: formData.status,
        paymentMethod: formData.paymentMethod,
      } : t));
      setActionMsg({ type: 'success', text: 'Transaction updated!' });
    } else {
      const newId = Math.max(0, ...txData.map(t => t.id)) + 1;
      const today = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
      const newTx: Transaction = {
        id: newId,
        userId: 0,
        userName: formData.userName,
        propertyTitle: formData.propertyTitle,
        amount,
        type: formData.type,
        paymentMethod: formData.paymentMethod,
        status: formData.status,
        date: today,
      };
      setTxData(prev => [...prev, newTx]);
      setActionMsg({ type: 'success', text: 'Transaction added!' });
    }

    setShowAddModal(false);
    setEditTarget(null);
    setFormData(emptyForm);
    clearMsg();
  }, [formData, editTarget, txData]);

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    setTxData(prev => prev.filter(t => t.id !== deleteTarget.id));
    setActionMsg({ type: 'success', text: `Transaction #TXN${String(deleteTarget.id).padStart(4, '0')} deleted.` });
    setDeleteTarget(null);
    clearMsg();
  }, [deleteTarget]);

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Toast */}
        {actionMsg && (
          <div className={`fixed top-4 right-4 z-[1100] px-4 py-3 rounded-xl text-sm font-semibold shadow-lg ${actionMsg.type === 'success' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'}`}>
            {actionMsg.text}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground-950">Transactions</h1>
            <p className="text-xs text-foreground-500">{filtered.length} transactions &middot; Total Revenue: <span className="text-teal-400 font-semibold">{'\u20B9'}{totalRevenue.toLocaleString('en-IN')}</span></p>
          </div>
          <button onClick={openCreate} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 cursor-pointer whitespace-nowrap">
            <i className="ri-add-line" /> Add Transaction
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-background-400 text-sm" />
            <input type="text" placeholder="Search by user, property, or ID..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
            <option value="">All Status</option>
            <option value="Paid">Paid</option><option value="Pending">Pending</option><option value="Failed">Failed</option><option value="Refunded">Refunded</option>
          </select>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="px-3 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
            <option value="">All Types</option>
            <option value="Listing Fee">Listing Fee</option><option value="Featured Upgrade">Featured Upgrade</option><option value="Subscription">Subscription</option>
          </select>
        </div>

        {/* Bulk Actions */}
        {selectedIds.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl border border-accent-200/70 bg-accent-50">
            <div className="flex items-center gap-2 text-sm font-semibold text-accent-900">
              <i className="ri-checkbox-multiple-line" />
              {selectedIds.length} selected
            </div>
            <div className="flex items-center gap-2 ml-auto flex-wrap">
              <button onClick={() => handleBulkStatus('Paid')} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-500/15 text-teal-700 text-xs font-semibold hover:bg-teal-500/25 cursor-pointer whitespace-nowrap">
                <i className="ri-check-double-line" /> Mark Paid
              </button>
              <button onClick={() => handleBulkStatus('Refunded')} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-500/15 text-blue-700 text-xs font-semibold hover:bg-blue-500/25 cursor-pointer whitespace-nowrap">
                <i className="ri-refund-2-line" /> Mark Refunded
              </button>
              <button onClick={handleBulkDelete} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-rose-500/15 text-rose-700 text-xs font-semibold hover:bg-rose-500/25 cursor-pointer whitespace-nowrap">
                <i className="ri-delete-bin-line" /> Delete
              </button>
              <button onClick={() => setSelectedIds([])} className="px-3 py-2 rounded-lg text-foreground-500 text-xs hover:text-foreground-950 cursor-pointer whitespace-nowrap">
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200/60">
                  <th className="w-10 px-4 py-3">
                    <input type="checkbox" checked={filtered.length > 0 && filtered.every(t => selectedIds.includes(t.id))} onChange={toggleSelectAll} className="w-4 h-4 accent-accent-500 cursor-pointer" title="Select all" />
                  </th>
                  <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">ID</th>
                  <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">User</th>
                  <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Property</th>
                  <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Amount</th>
                  <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Type</th>
                  <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Payment</th>
                  <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Status</th>
                  <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Date</th>
                  <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={10} className="px-4 py-12 text-center text-sm text-foreground-500">No transactions found.</td></tr>
                ) : (
                  filtered.map(t => (
                    <tr key={t.id} className={`border-b border-background-200/50 hover:bg-background-200/80 transition-colors ${selectedIds.includes(t.id) ? 'bg-accent-50/60' : ''}`}>
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={selectedIds.includes(t.id)} onChange={() => toggleSelect(t.id)} className="w-4 h-4 accent-accent-500 cursor-pointer" />
                      </td>
                      <td className="px-4 py-3 text-xs text-foreground-500 font-mono">#TXN{t.id.toString().padStart(4, '0')}</td>
                      <td className="px-4 py-3 text-sm text-foreground-950">{t.userName}</td>
                      <td className="px-4 py-3 text-xs text-foreground-500 max-w-[180px] truncate">{t.propertyTitle}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-foreground-950 whitespace-nowrap">{'\u20B9'}{t.amount.toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${typeBadge(t.type)}`}>{t.type}</span></td>
                      <td className="px-4 py-3 text-xs text-foreground-500">{t.paymentMethod}</td>
                      <td className="px-4 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusBadge(t.status)}`}>{t.status}</span></td>
                      <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">{t.date}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(t)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-background-400 hover:text-accent-700 cursor-pointer" title="Edit"><i className="ri-edit-line" /></button>
                          <button onClick={() => setDeleteTarget(t)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-background-400 hover:text-rose-400 cursor-pointer" title="Delete"><i className="ri-delete-bin-line" /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add / Edit Transaction Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setShowAddModal(false); setEditTarget(null); }} />
          <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-foreground-950">{editTarget ? 'Edit Transaction' : 'Add Transaction'}</h2>
              <button onClick={() => { setShowAddModal(false); setEditTarget(null); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 cursor-pointer"><i className="ri-close-line" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">User *</label>
                <input type="text" placeholder="User name" value={formData.userName} onChange={e => setFormData(f => ({ ...f, userName: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Property *</label>
                <input type="text" placeholder="Property title" value={formData.propertyTitle} onChange={e => setFormData(f => ({ ...f, propertyTitle: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Amount (₹) *</label>
                  <input type="number" placeholder="0" value={formData.amount} onChange={e => setFormData(f => ({ ...f, amount: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Type</label>
                  <select value={formData.type} onChange={e => setFormData(f => ({ ...f, type: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                    <option>Listing Fee</option><option>Featured Upgrade</option><option>Subscription</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Status</label>
                  <select value={formData.status} onChange={e => setFormData(f => ({ ...f, status: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                    <option>Paid</option><option>Pending</option><option>Failed</option><option>Refunded</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Payment Method</label>
                  <select value={formData.paymentMethod} onChange={e => setFormData(f => ({ ...f, paymentMethod: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                    <option>UPI</option><option>Net Banking</option><option>Card</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Notes</label>
                <textarea rows={2} placeholder="Optional notes..." value={formData.notes} onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50 resize-none" />
              </div>
              <button onClick={handleSubmit} className="w-full py-3 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 cursor-pointer whitespace-nowrap">
                {editTarget ? 'Update Transaction' : 'Save Transaction'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="relative z-10 w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 space-y-4">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-rose-500/15 flex items-center justify-center mb-3">
                <i className="ri-delete-bin-line text-rose-400 text-xl" />
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground-950">Delete Transaction?</h3>
              <p className="text-sm text-foreground-500 mt-1">This will permanently delete transaction <strong className="text-foreground-950">#TXN{String(deleteTarget.id).padStart(4, '0')}</strong>. This cannot be undone.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 rounded-xl border border-background-200/60 text-sm text-foreground-500 hover:text-foreground-950 cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={handleDelete} className="flex-1 py-2.5 rounded-xl bg-rose-500 text-white text-sm font-semibold hover:bg-rose-600 cursor-pointer whitespace-nowrap">Delete</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}